<script setup lang="ts">
import { computed } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import useToast, { type Toast, type ToastPosition } from '@/composables/useToast'
import { vPress } from '@/directives/press'

const { toasts, dismissToast, toastInsetBottom } = useToast()
const isMobile = useMediaQuery('(max-width: 639px)')

// Non-error toasts without an action are suppressed on mobile at creation time; also filter here
// so a desktop→mobile resize hides any already-visible success/info toasts.
const visibleToasts = computed(() =>
    isMobile.value ? toasts.value.filter((t) => t.type === 'error' || t.action || t.actions?.length) : toasts.value,
)

const leftToasts = computed(() =>
    visibleToasts.value.filter((t) => (t.position ?? 'right') === 'left'),
)
const rightToasts = computed(() =>
    visibleToasts.value.filter((t) => (t.position ?? 'right') !== 'left'),
)

const toastAnchorStyle = computed(() => ({
    bottom: `calc(${16 + toastInsetBottom.value}px + env(safe-area-inset-bottom, 0px))`,
}))

function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Freeze the leaving slot's pixel height so it can ease to 0 in-flow
 *  (bottom-anchored stacks jump if the leaving toast is position:absolute). */
function onToastBeforeLeave(el: Element) {
    if (prefersReducedMotion()) return
    const node = el as HTMLElement
    node.style.height = `${node.offsetHeight}px`
}

function toastActions(toast: Toast) {
    if (toast.actions?.length) return toast.actions
    return toast.action ? [toast.action] : []
}

function handleAction(toast: Toast, action: { handler?: () => void }) {
    if (action?.handler) {
        action.handler()
    }
    dismissToast(toast.id)
}

function progressLabel(toast: Toast) {
    const total = toast.progress?.total ?? 0
    const current = toast.progress?.current ?? 0
    const remaining = Math.max(0, total - current)
    const noun = toast.progressNoun ?? 'draft'
    return `Sending ${remaining} ${noun}${remaining === 1 ? '' : 's'}...`
}
</script>

<template>
    <Teleport to="body">
        <!-- Phones: one centred capsule stack (both sides merge), 44px pill actions, above whatever owns the
             bottom edge (the chat dock sets toastInsetBottom while it's up). -->
        <div v-if="isMobile" class="ptoast-anchor" :style="toastAnchorStyle">
            <TransitionGroup
                name="app-toast"
                tag="div"
                class="app-toast-stack is-phone"
                @before-leave="onToastBeforeLeave">
                <div
                    v-for="toast in visibleToasts"
                    :key="toast.id"
                    class="app-toast-item">
                    <div
                        class="app-toast-card ptoast"
                        :class="`is-${toast.type}`"
                        :role="toast.type === 'error' ? 'alert' : 'status'">
                        <span class="ptoast-icon" aria-hidden="true">
                            <svg v-if="toast.variant === 'progress' || toast.variant === 'loading'" class="w-5 h-5 animate-spin text-orange-dark" viewBox="0 0 24 24" fill="none">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                                <path class="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <CheckCircleIcon v-else-if="toast.type === 'success'" class="w-5 h-5 text-green-500" />
                            <ExclamationTriangleIcon v-else-if="toast.type === 'error'" class="w-5 h-5 text-red-500" />
                            <InformationCircleIcon v-else class="w-5 h-5 ptoast-info" />
                        </span>
                        <p class="ptoast-msg">{{ toast.variant === 'progress' ? progressLabel(toast) : toast.message }}</p>
                        <button
                            v-for="(action, idx) in toastActions(toast)"
                            :key="`${toast.id}-action-${idx}`"
                            v-press.pill
                            type="button"
                            class="ptoast-action"
                            :class="{ 'is-secondary': action.variant === 'secondary' }"
                            @click="handleAction(toast, action)">
                            {{ action.label }}
                        </button>
                        <button
                            v-if="!toastActions(toast).length"
                            v-press.icon
                            type="button"
                            class="ptoast-close"
                            aria-label="Dismiss"
                            @click="dismissToast(toast.id)">
                            <XMarkIcon class="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </TransitionGroup>
        </div>
        <template v-else>
        <div
            v-for="side in (['left', 'right'] as ToastPosition[])"
            :key="side"
            class="fixed z-[var(--z-toast,10080)] pointer-events-none max-w-sm"
            :class="side === 'left' ? 'left-4' : 'right-4'"
            :style="toastAnchorStyle">
            <TransitionGroup
                name="app-toast"
                tag="div"
                class="app-toast-stack"
                @before-leave="onToastBeforeLeave">
                <div
                    v-for="toast in (side === 'left' ? leftToasts : rightToasts)"
                    :key="toast.id"
                    class="app-toast-item">
                    <div
                        class="app-toast-card pointer-events-auto w-80 rounded-lg border shadow-lg p-4 flex gap-3"
                        :class="{
                            'bg-white border-neutral-200': toast.type === 'info',
                            'bg-white border-green-200': toast.type === 'success',
                            'bg-white border-red-200': toast.type === 'error',
                        }">
                        <div class="flex-shrink-0 mt-0.5">
                            <svg v-if="toast.variant === 'progress' || toast.variant === 'loading'" class="w-5 h-5 animate-spin text-orange-dark" viewBox="0 0 24 24" fill="none">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                                <path class="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <CheckCircleIcon v-else-if="toast.type === 'success'" class="w-5 h-5 text-green-500" />
                            <ExclamationTriangleIcon v-else-if="toast.type === 'error'" class="w-5 h-5 text-red-500" />
                            <InformationCircleIcon v-else class="w-5 h-5 text-neutral-500" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="relative pr-5">
                                <p v-if="toast.variant === 'progress'" class="text-sm text-primary leading-[1.45]">
                                    {{ progressLabel(toast) }}
                                </p>
                                <p v-else class="text-sm text-primary leading-[1.45]">{{ toast.message }}</p>
                                <button
                                    type="button"
                                    class="absolute top-0 right-0 text-neutral-400 hover:text-neutral-600 transition-colors"
                                    @click="dismissToast(toast.id)">
                                    <XMarkIcon class="w-4 h-4" />
                                </button>
                            </div>
                            <div v-if="toastActions(toast).length" class="flex flex-wrap justify-end items-center gap-x-2 gap-y-1.5 mt-2.5">
                                <button
                                    v-for="(action, idx) in toastActions(toast)"
                                    :key="`${toast.id}-action-${idx}`"
                                    type="button"
                                    class="cursor-pointer"
                                    :class="action.variant === 'secondary'
                                        ? 'text-xs font-medium text-secondary hover:text-primary px-2 py-1.5 transition-colors'
                                        : 'button-primary sm !min-w-max'"
                                    @click="handleAction(toast, action)">
                                    {{ action.label }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </TransitionGroup>
        </div>
        </template>
    </Teleport>
</template>

<style scoped>
.app-toast-stack {
    position: relative;
    width: 20rem;
}

.app-toast-item {
    /* Overlap so multiple pitch/success toasts don't fan down the screen. */
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
}

.app-toast-item:nth-last-child(1) { z-index: 3; }
.app-toast-item:nth-last-child(2) {
    z-index: 2;
    transform: translateY(-6px) scale(0.98);
    opacity: 0.92;
}
.app-toast-item:nth-last-child(3) {
    z-index: 1;
    transform: translateY(-12px) scale(0.96);
    opacity: 0.75;
}
.app-toast-item:nth-last-child(n+4) {
    display: none;
}

.app-toast-enter-active {
    transition:
        opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1),
        transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.app-toast-leave-active {
    overflow: hidden;
    pointer-events: none;
    transition:
        height 0.38s cubic-bezier(0.16, 1, 0.3, 1),
        margin-top 0.38s cubic-bezier(0.16, 1, 0.3, 1);
}

.app-toast-leave-active .app-toast-card {
    transform-origin: center bottom;
    transition:
        opacity 0.32s cubic-bezier(0.16, 1, 0.3, 1),
        transform 0.32s cubic-bezier(0.16, 1, 0.3, 1);
}

.app-toast-enter-from {
    opacity: 0;
    transform: translateY(14px) scale(0.96);
}

.app-toast-leave-to {
    height: 0 !important;
    margin-top: 0 !important;
}

.app-toast-leave-to .app-toast-card {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
}

.app-toast-move {
    transition: transform 0.36s cubic-bezier(0.16, 1, 0.3, 1);
}

/* ── Phones: a centred capsule ─────────────────────────────────────────────────────────────── */
.ptoast-anchor {
    position: fixed;
    left: 16px;
    right: 16px;
    z-index: var(--z-toast, 10080);
    display: flex;
    justify-content: center;
    pointer-events: none;
    transition: bottom var(--lh-dur-push, 340ms) var(--lh-ease-ios, cubic-bezier(0.32, 0.72, 0, 1));
}
.app-toast-stack.is-phone {
    width: 100%;
    max-width: 420px;
}
.ptoast {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 56px;
    padding: 6px 6px 6px 16px;
    border-radius: 28px;
    background: var(--lh-surface, #fff);
    color: var(--lh-text-1, #242424);
    box-shadow:
        0 0 0 0.5px rgba(15, 15, 15, 0.08),
        0 10px 30px rgba(15, 15, 15, 0.14),
        0 2px 6px rgba(15, 15, 15, 0.06);
    pointer-events: auto;
}
html.dark .ptoast {
    /* Background: theme.css's html.dark .app-toast-card (surface). */
    box-shadow:
        0 0 0 0.5px rgba(255, 255, 255, 0.1),
        0 12px 32px rgba(0, 0, 0, 0.5);
}
.ptoast-icon {
    display: flex;
    flex: none;
}
.ptoast-info {
    color: var(--lh-text-3, #737373);
}
.ptoast-msg {
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 6px 0;
    font-size: 15px;
    line-height: 1.35;
}
.ptoast-action {
    flex: none;
    min-width: 64px;
    min-height: 44px;
    padding: 0 18px;
    border: 0;
    border-radius: 999px;
    background: var(--lh-accent, #fb4c1f);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
}
.ptoast-action.is-secondary {
    min-width: 0;
    padding: 0 12px;
    background: transparent;
    color: var(--lh-text-2, #525252);
}
.ptoast-close {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--lh-text-3, #737373);
    cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
    .ptoast-anchor,
    .app-toast-enter-active,
    .app-toast-leave-active,
    .app-toast-leave-active .app-toast-card,
    .app-toast-move {
        transition: none;
    }
}
</style>
